import React, { useState, useRef, useEffect } from "react";
import { Bot, Settings, Trash2, ExternalLink, Send, Loader2 } from "lucide-react";
import { AiMessage } from "../utils/helpers";
import { useAppContext } from "../context/AppContext";

export default function AiVet() {
  const { t, activePet, pets, activePetId, setActivePetId } = useAppContext();

  const [geminiKey, setGeminiKey] = useState(() => localStorage.getItem("mypaw_gemini_key") || "");
  const [keyInput, setKeyInput] = useState("");
  
  const [aiMessages, setAiMessages] = useState<AiMessage[]>(() => {
    try {
      const saved = localStorage.getItem("mypaw_ai_chat");
      if (!saved) return [];
      return JSON.parse(saved);
    } catch(e) { return []; }
  });

  const [aiInput, setAiInput] = useState("");
  const [isAiTyping, setIsAiTyping] = useState(false);
  const aiChatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTimeout(() => aiChatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 150);
  }, [aiMessages.length]);

  const clearAiChat = () => {
    if (window.confirm(t.clearAiAlert)) { setAiMessages([]); localStorage.removeItem("mypaw_ai_chat"); }
  };

  const handleSendAiMessage = async () => {
    if (!aiInput.trim() || !geminiKey) return;
    const userMsg: AiMessage = { role: "user", text: aiInput.trim() };
    const currentChat = [...aiMessages, userMsg]; setAiMessages(currentChat); setAiInput(""); setIsAiTyping(true);
    try {
      const sys = t.aiPrompt.replace("{name}", activePet.name).replace("{breed}", activePet.breed || t.notSpec).replace("{weight}", activePet.weight || t.notSpec).replace("{diagnoses}", activePet.diagnoses?.join(", ") || "-").replace("{meds}", activePet.meds?.join(", ") || "-").replace("{allergies}", activePet.allergies?.join(", ") || "-").replace("{features}", activePet.features || "-");
      const messages = currentChat.map((m) => ({ role: m.role === "model" ? "assistant" : "user", content: m.text }));
      messages.unshift({ role: "system", content: sys });
      
      const res = await fetch("https://api.groq.com/openai/v1/chat/completions", { 
        method: "POST", headers: { "Authorization": `Bearer ${geminiKey.trim()}`, "Content-Type": "application/json" }, 
        body: JSON.stringify({ model: "openai/gpt-oss-120b", messages }) 
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error.message);
      
      const botReply = data.choices[0].message.content;
      setAiMessages([...currentChat, { role: "model", text: botReply }]); 
      localStorage.setItem("mypaw_ai_chat", JSON.stringify([...currentChat, { role: "model", text: botReply }]));
    } catch (e: any) { 
      setAiMessages([...currentChat, { role: "model", text: `❌ Error: ${e.message}` }]); 
    } finally { setIsAiTyping(false); }
  };

  return (
    <div className="flex flex-col h-[100dvh] absolute inset-0 z-20 bg-[var(--bg-soft)] animate-fade-in">
      <div className="bg-gradient-to-br from-[#a073e7] to-[#b991f8] px-5 py-4 shrink-0 pt-12 text-white flex justify-between items-center shadow-md">
        <h2 className="font-extrabold text-xl flex items-center gap-2"><Bot size={22} /> {t.aiVet}</h2>
        <button onClick={() => { setGeminiKey(""); localStorage.removeItem("mypaw_gemini_key"); }} className="opacity-60"><Settings size={18}/></button>
      </div>
      
      {geminiKey && (
        <div className="bg-[var(--bg-card)] border-b border-[var(--border-color)] px-4 py-2 shrink-0 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3 overflow-x-auto no-scrollbar">
            <span className="text-xs font-bold text-[var(--text-muted)] whitespace-nowrap">{t.patient}:</span>
            {pets.map((p) => (
              <button key={p.id} onClick={() => setActivePetId(p.id)} className={`flex-shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-full transition-all border ${activePetId === p.id ? "bg-[#a073e7] text-white border-[#a073e7] shadow-sm" : "bg-[var(--bg-soft)] text-[var(--text-muted)] border-[var(--border-color)]"}`}>
                <div className="w-5 h-5 rounded-full bg-[var(--accent-soft)] flex items-center justify-center font-bold text-[9px] text-[#a073e7] overflow-hidden">{p.photo ? <img src={p.photo} className="w-full h-full object-cover" /> : p.name[0]}</div>
                <span className="text-xs font-bold pr-1">{p.name}</span>
              </button>
            ))}
          </div>
          <button onClick={clearAiChat} className="p-2 text-[var(--red-text)] hover:bg-[var(--red-soft)] rounded-full transition-colors"><Trash2 size={18}/></button>
        </div>
      )}

      {!geminiKey ? (
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <Bot size={60} className="text-[#a073e7] mb-4" />
          <h3 className="font-bold text-[var(--text-main)] mb-2">Groq AI</h3>
          <p className="text-xs text-[var(--text-muted)] mb-6">{t.pasteKey}</p>
          <input type="password" value={keyInput} onChange={e => setKeyInput(e.target.value)} placeholder="Groq API Key..." className="w-full h-12 px-4 rounded-xl border border-[var(--border-color)] bg-[var(--bg-card)] text-[var(--text-main)] mb-4 text-center outline-none" />
          <button onClick={() => { setGeminiKey(keyInput); localStorage.setItem("mypaw_gemini_key", keyInput); }} className="w-full py-4 bg-[#a073e7] text-white font-bold rounded-xl shadow-lg active:scale-95 transition-all mb-4">{t.activate}</button>
          <a href="https://console.groq.com/keys" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1 text-xs font-bold text-[#a073e7] bg-[var(--accent-soft)] px-4 py-2.5 rounded-xl active:scale-95 transition-all w-full shadow-sm border border-[var(--border-accent)]"><ExternalLink size={14} /> {t.getFreeKey}</a>
        </div>
      ) : (
        <>
          <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4 no-scrollbar pb-32">
            {aiMessages.length === 0 && <div className="text-center p-10 text-[var(--text-muted)] text-sm">{t.askAbout} {activePet.name} 🐾</div>}
            {aiMessages.map((msg, idx) => (
              <div key={idx} className={`max-w-[85%] p-4 rounded-2xl shadow-sm whitespace-pre-wrap ${msg.role === "model" ? 'self-start bg-[var(--bg-card)] border border-[var(--border-color)] text-[var(--text-main)] rounded-tl-none' : 'self-end bg-[#a073e7] text-white rounded-tr-none'}`}>{msg.text}</div>
            ))}
            {isAiTyping && <div className="text-[#a073e7] text-xs font-bold animate-pulse ml-2 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> {t.thinking}</div>}
            <div ref={aiChatEndRef} className="h-1 shrink-0" />
          </div>
          <div className="fixed bottom-[80px] left-0 right-0 bg-[var(--bg-card)] p-3 border-t border-[var(--border-color)] z-30">
            <div className="flex items-center gap-2 bg-[var(--bg-soft)] border border-[var(--border-accent)] rounded-full px-1 py-1">
              <input value={aiInput} onChange={e => setAiInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSendAiMessage()} placeholder={`${t.askAbout} ${activePet.name}...`} className="flex-1 bg-transparent px-4 outline-none text-sm h-10 text-[var(--text-main)]" />
              <button onClick={handleSendAiMessage} className="p-2.5 bg-[#a073e7] rounded-full text-white shadow-md active:scale-95"><Send size={18} /></button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}